<template>
  <div class="offer-files__container">
    <div v-if="downloadOnly" class="spacey-8 d-contents">
      <DownloadFile
        v-for="(file, idx) in offer.files"
        :key="idx"
        class="offer-file"
        :file="file"
        is-extended
      />
    </div>
    <div v-else class="spacey-8 d-contents">
      <AttachedFile
        v-for="(file, idx) in uploadedFiles"
        :key="idx"
        :file="file"
        :loading="file.uploading"
        class="offer-file"
        show-tooltip
        @delete="handleDeletion"
      />
      <FileUpload :max-size="100" @select="uploadFiles">
        <Button size="small" left-icon="paperclip" type="ghost" fill :loading="loading">
          {{ $t('Attach files') }}
        </Button>
      </FileUpload>
    </div>
  </div>
</template>

<script>
  import DownloadFile from '@/components/files/DownloadFile';
  import AttachedFile from '@/components/files/AttachedFile';
  import FileUpload from '@/components/ui/FileUpload';
  import Button from '@/components/ui/Button';
  import axios from 'axios';
  import request from '@/utils/api/request';
  import { mapActions } from 'vuex';

  export default {
    name: 'OfferFiles',
    components: {
      DownloadFile,
      AttachedFile,
      FileUpload,
      Button,
    },
    props: {
      offer: {
        type: Object,
        required: true,
      },
      downloadOnly: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loading: false,
        fileIdx: 1,
        uploadedFiles: [],
      };
    },
    computed: {
      isOldOrArchived() {
        return ['old', 'archived'].includes(this.offer.status);
      },
    },
    created() {
      this.uploadedFiles = this.offer.files;
    },
    methods: {
      ...mapActions('confirmModal', ['openModal']),
      uploadFiles(files) {
        files.forEach((file) => this.createFile(file));
      },
      async createFile(file) {
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();

        let fd = new FormData();
        fd.set('file', file);

        let newFile = {
          file,
          source,
          uploading: true,
          percent: 0,
          fileIdx: this.fileIdx++,
          id: 0,
          formatError: false,
          name: file.name,
        };

        const config = {
          onUploadProgress: (event) => {
            newFile.percent = Math.round((event.loaded * 100) / event.total);
          },
          cancelToken: source.token,
        };

        this.uploadedFiles.push(newFile);

        try {
          let createdFile = await request.post(
            `/incoming_tenders/offers/versions/${this.offer.id}/files/`,
            fd,
            config
          );
          newFile.id = createdFile.data.id;
          newFile.name = createdFile.data.name;
        } catch (e) {
          this.formatError = true;
        }

        newFile.uploading = false;
      },
      removeFile(id) {
        this.uploadedFiles = this.uploadedFiles.filter((uploadedFile) => uploadedFile.id !== id);
      },
      handleDeletion(file) {
        if (file.uploading) {
          file.source.cancel();
          this.removeFile(file.id);
        } else {
          this.openModal({
            title: `${this.$t('Delete file')} “${file.name}”?`,
            description: this.$t('This action cannot be undone.'),
            revertButtons: true,
            actionText: this.$t('Delete'),
            cancelText: this.$t('Return'),
            style: 'danger',
            action: () => {
              this.deleteFileHandler(file.id);
            },
          });
        }
      },
      async deleteFileHandler(fileId) {
        try {
          this.removeFile(fileId);
          await request.delete(
            `/incoming_tenders/offers/versions/${this.offer.id}/files/${fileId}/`
          );
        } catch (e) {
          console.error(e);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .offer-files__container {
    width: 100%;
  }
</style>
